import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, Input, ElementRef, ViewChild, HostListener, Optional, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ChatbotService } from "../../../services/chatbot-services/chatbot.service";
import { Subscription } from 'rxjs';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { MetadataConfigService, MetadataFieldConfig } from '../../../services/metadata-config/metadata-config.service';
import { LoggerService } from '../../../services/logger-services/logger.service';
import { SourceContext, ApiResponse, SourceData, ConfigData, FilterOption, CountryDisplay } from '../../models/source-panel.models';
import { ISourcePanelApi, SOURCE_PANEL_API } from '../../services/source-panel-api/source-panel-api.interface';
import { RegulationNextService, resolveRegulationVersion } from '../../../services/regulation-next/regulation-next.service';
import { formatAttachmentDisplayName } from '../../../shared/utils/attachment-display-name.util';
import {
  DOC_TABLE_CELL_PLACEHOLDER,
  formatDocReleaseDate,
  formatDocTableCell,
  getRegulationDocumentRows,
  RegulationDocumentRow,
} from '../../../shared/utils/regulation-document-rows.util';
import { normalizeRegulationsCitation } from '../../agents/services/regulation-citation/regulation-citation.util';

/**
 * View modes for source display
 */
type ViewMode = 'compact' | 'detailed';

/**
 * SourcePanelComponent handles the display and management of source metadata
 * for chatbot conversations, providing functionality to view, search, and download
 * related documents and insights.
 */
@Component({
  selector: 'app-source-panel',
  templateUrl: './source-panel.component.html',
  styleUrls: ['./source-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourcePanelComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;

  // Filter to relevance mapping constant - uses translation keys
  public static readonly FILTER_RELEVANCE_MAP: Record<string, string> = {
    'MOST_RELEVANT': 'high',
    'MEDIUM_RELEVANT': 'medium',
    'LESS_RELEVANT': 'low'
  };

  // Filter options for dropdown - initialized in constructor with translated labels
  public filterOptions: FilterOption[] = [];

  // Constants
  public static readonly DEFAULT_VIEW_MODE: ViewMode = 'detailed';
  public static readonly INSIGHT_VIEW_PATH = "/regulations?modulePath=/Insights/view-regulation?tab=true&insid=";
  public static readonly SUCCESS_STATUS_CODE = 200;
  public static readonly SUCCESS_STATUS_STRING = "200";
  public static readonly MAX_COUNTRIES_DISPLAY = 2;
  /** Accepted relevance bands, uppercase, as emitted by agent sources via `band`. */
  private static readonly RELEVANCE_BANDS: readonly string[] = ['HIGH', 'MEDIUM', 'LOW'];
  /** Agent-only source fields that carry no meaning for the reader and must stay out of the detailed view. */
  private static readonly AGENT_HIDDEN_FIELDS: readonly string[] = ['status', 'accuracy', 'insight_id'];
  /** Agent-only source fields holding document file names / per-document metadata. */
  private static readonly AGENT_DOCUMENT_FIELDS: readonly string[] = ['associated_documents', 'attachment_names', 'document_name'];
  /** Agent alias of configured `ref_url` (References); folded then deleted so it is not rediscovered as "Ref Urls". */
  private static readonly AGENT_REF_URLS_FIELD = 'ref_urls';
  /** Max document rows shown before "+N more documents" expand control. */
  public readonly DOC_TABLE_PREVIEW_COUNT = 3;
  /** Max reference links shown before "+more" expand control. */
  public readonly REF_LINKS_PREVIEW_COUNT = 2;
  /** Placeholder for Ver. / Released when per-document fields are empty. */
  public readonly DOC_TABLE_PLACEHOLDER = DOC_TABLE_CELL_PLACEHOLDER;

  private readonly SourcePanelError = (error: any) => 
    `['SourcePanelComponent] - ${JSON.stringify(error)}`;

  /** Source item keys with documents table expanded beyond preview count. */
  private readonly expandedDocumentKeys = new Set<string>();
  /** Composite keys (`itemKey::fieldKey`) with reference links expanded beyond preview. */
  private readonly expandedReferenceKeys = new Set<string>();

  // Output Events
  @Output() closePanelEvent = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<string | null>();

  // Input Properties
  @Input() sourceContext: SourceContext | null = null;
  @Input() configdata: ConfigData | null = null;
  @Input() tenantId: string | null = null;
  @Input() domainId: string | null = null;

  // Public Properties
  public sourcesCount: number = 0;
  public sources: SourceData[] = [];
  public sourcesData: SourceData[] = [];
  public searchQuery: string = '';
  public isDocChat: boolean = true;
  public viewMode: ViewMode = SourcePanelComponent.DEFAULT_VIEW_MODE;
  public isLoading: boolean = false;
  public chatHistoryId: string | null = null;

  // Relevancy Properties
  public isRelevancyDropdownOpen: boolean = false;
  public relevancyData = {
    mostRelevant: 0,
    mediumRelevant: 0,
    lessRelevant: 0
  };

  // Filter Properties
  public isFilterDropdownOpen: boolean = false;
  public selectedFilter: string = 'ALL';
  public sortBy: string = "";

  // Sorting Dropdown Properties
  public options = [
    { value: 'relevance', label: 'Relevence' },
    { value: 'release_date', label: 'Date' }
  ];
  public selectedOption: string | null= 'relevance';
  public isSortDropdownOpen: boolean = false;

  // New property to track if relevancy is enabled
  public isRelevancyEnabled: boolean = false;
  public isDateEnabled: boolean = false;

  /** Cached result of getSourcesWithKeys() – rebuilt only when sources data changes, not on every change detection cycle. */
  public cachedSourcesWithKeys: { item: any, key: string }[] = [];

  // Tooltip message for disabled relevancy features
  public readonly relevancyDisabledTooltip: string = this.translate.instant('RELEVANCY_DISABLED_TOOLTIP');

  // Computed Properties
  public get InsightViewURL(): string {
    return this.configdata?.FusionAppUIUrl
      ? `${this.configdata.FusionAppUIUrl}${SourcePanelComponent.INSIGHT_VIEW_PATH}`
      : SourcePanelComponent.INSIGHT_VIEW_PATH;
  }

  // Public Properties
  public subscriptions: Subscription = new Subscription();

  /**
   * Resolved API for source panel: optional agent API or ChatbotService (chatbot).
   */
  private get sourceApi(): ISourcePanelApi {
    return this.sourcePanelApi ?? this.chatbotService;
  }

  /**
   * True when the panel is rendered inside an agent (agent API injected), false for chatbot.
   */
  private get isAgentSourceApi(): boolean {
    return !!this.sourcePanelApi;
  }

  /**
   * Constructor - Initializes component with required services
   * @param chatbotService - Service for chatbot operations and API calls
   * @param languageService - Service for handling language changes
   * @param translate - Angular translate service for internationalization
   * @param elementRef - Element reference for DOM manipulation
   * @param metadataConfigService - Service for metadata field configuration and processing
   * @param loggerService - Service for error logging and debugging
   * @param sourcePanelApi - Optional agent-specific API (e.g. RegulationAgentSourceApiService); when provided, used instead of chatbotService for source panel calls
   */
  constructor(
    public chatbotService: ChatbotService,
    public languageService: LanguageChangeService,
    public translate: TranslateService,
    private readonly elementRef: ElementRef,
    private readonly metadataConfigService: MetadataConfigService,
    private readonly loggerService: LoggerService,
    private readonly regulationNextService: RegulationNextService,
    private readonly cdr: ChangeDetectorRef,
    @Optional() @Inject(SOURCE_PANEL_API) private readonly sourcePanelApi?: ISourcePanelApi
  ) {
    this.initializeFilterOptions();
  }

  /**
   * Initialize filter options with translation keys
   * The label is the translation key which will be translated in the template
   */
  private initializeFilterOptions(): void {
    this.filterOptions = [
      { label: 'ALL', dotClass: undefined },
      { label: 'MOST_RELEVANT', dotClass: 'high' },
      { label: 'MEDIUM_RELEVANT', dotClass: 'medium' },
      { label: 'LESS_RELEVANT', dotClass: 'low' }
    ];
  }
  /**
   * Angular lifecycle hook - Component initialization
   * Initializes component state, sets default values, and fetches source metadata
   * @returns void
   */
  ngOnInit(): void {
    try{
    this.chatHistoryId = this.sourceContext?.chatHistoryId || null;
    // Reset filter to default 'All' on initialization
    this.selectedFilter = 'ALL';
    this.selectedOption = 'relevance';
    this.sortBy = this.selectedOption ?? '';
    this.viewMode = SourcePanelComponent.DEFAULT_VIEW_MODE
    this.fetchSourceMetadata(this.tenantId, this.searchQuery, this.chatHistoryId, this.getRelevanceValue(this.selectedFilter), this.sortBy);
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"ngOnInit")
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   * Cleans up subscriptions to prevent memory leaks
   * @returns void
   */
  ngOnDestroy(): void {
    try{
      this.subscriptions.unsubscribe();
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"ngOnDestroy")
    }
  }

  /**
   * Angular lifecycle hook - Input property changes
   * Only refetches when sourceContext or tenantId change (not when configdata/domainId reference changes),
   * to avoid repeated API calls from parent getters returning new object references every change detection.
   */
  ngOnChanges(changes: SimpleChanges): void {
    try {
      this.chatHistoryId = this.sourceContext?.chatHistoryId || null;
      this.searchQuery = "";
      this.selectedFilter = 'ALL';
      this.selectedOption = 'relevance';
      this.sortBy = this.selectedOption ?? '';
      this.viewMode = SourcePanelComponent.DEFAULT_VIEW_MODE;

      const sourceContextChanged = changes['sourceContext']?.currentValue !== changes['sourceContext']?.previousValue;
      const tenantIdChanged = changes['tenantId']?.currentValue !== changes['tenantId']?.previousValue;
      const isFirstChange = changes['sourceContext']?.firstChange === true;
      const shouldRefetch = !isFirstChange && (sourceContextChanged || tenantIdChanged) && this.chatHistoryId;

      if (shouldRefetch) {
        this.fetchSourceMetadata(this.tenantId, this.searchQuery, this.chatHistoryId, this.getRelevanceValue(this.selectedFilter), this.sortBy);
      } else if (!this.chatHistoryId && (sourceContextChanged || tenantIdChanged)) {
        this.loggerService.logError("Not calling fetchSourceMetadata - chatHistoryId is null/undefined", "ngOnChanges");
      }
    } catch (error: any) {
      this.loggerService.logError(this.SourcePanelError(error), "ngOnChanges");
    }
  }

  /**
   * Fetch source metadata from the backend service
   * @param domainId - Domain identifier
   * @param tenantId - Tenant identifier
   * @param searchTerm - Optional search term for filtering
   * @param chatHistoryId - Chat history identifier
   */
  public fetchSourceMetadata(
    tenantId: string | null,
    searchTerm: string = "",
    chatHistoryId: string | null = null,
    relevance: string | null = null,
    sort_by: string = ""
  ): void {
    try{
    if (!this.validateFetchParameters(tenantId, chatHistoryId)) {
      this.loggerService.logError("Invalid parameters for fetching metadata","fetchSourceMetadata")
      // Show user-friendly message instead of silent failure
      this.isLoading = false;
      this.sourcesData = [];
      // You might want to show a toast message here or update UI to show the error
      return;
    }

    this.isLoading = true;

    const subscription = this.sourceApi.searchMetadata(
      tenantId!,
      searchTerm,
      chatHistoryId!,
      relevance,
      sort_by
    ).subscribe({
      next: (response: ApiResponse) => this.handleMetadataResponse(response),
      error: (error: any) => this.handleMetadataError(error)
    });

    this.subscriptions.add(subscription);
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"fetchSourceMetadata")
    }
  }

  /**
   * Validate parameters required for fetching metadata
   * @param tenantId - Tenant identifier (required)
   * @param chatHistoryId - Chat history identifier (required)
   * @returns boolean - True if parameters are valid, false otherwise
   */
  public validateFetchParameters(
    tenantId: string | null,
    chatHistoryId: string | null
  ): boolean {
    return !!(tenantId && chatHistoryId);
  }

  /**
   * Handle successful metadata response from API
   * Processes and transforms API response data into component-compatible format
   * @param response - API response containing metadata information
   * @returns void
   */
  public handleMetadataResponse(response: ApiResponse): void {
    try{
    this.isLoading = false;

    if (
      response.status === SourcePanelComponent.SUCCESS_STATUS_CODE &&
      response.body?.metadata &&
      typeof response.body.metadata === 'object'
    ) {
      // Handle the new API response structure where metadata contains numbered keys (1, 2, 3, etc.)
      // Each numbered key contains an object with: key, id, module_name, metadata, relevance_score, explanation, index
      const metadata = response.body.metadata;
      const metadataKeys = Object.keys(metadata);
      
      // Transform the new structure to the format our component expects
      this.sourcesData = metadataKeys.map(key => {
        const item = metadata[key];
        const normalizedItem = normalizeRegulationsCitation(item) ?? item;
        // Extract the nested metadata and flatten some properties to maintain backward compatibility
        const flattened: SourceData = {
          ...normalizedItem,
          // For backward compatibility, also set ins_id and rtq_id at the top level
          ins_id: normalizedItem['ins_id'] ||
            (normalizedItem['module_name'] === 'regulations' ? normalizedItem['id'] : undefined),
          rtq_id: normalizedItem['module_name'] === 'rtq' ? normalizedItem['id'] : undefined,
          // Extract some common fields from nested metadata for easier access
          title: this.readMetadataField(normalizedItem, 'title') ||
            this.readMetadataField(normalizedItem, 'insight_title') ||
            this.readMetadataField(normalizedItem, 'query_title') ||
            '',
          countries: this.readMetadataField(normalizedItem, 'countries') ||
            this.readMetadataField(normalizedItem, 'insight_markets') ||
            [],
          // Preserve the original key for ordering
          original_key: key
        };
        return this.isAgentSourceApi ? this.normalizeAgentSourceItem(flattened) : flattened;
      });

      // Check if any source has relevance_score (API flag or contribution_level / band on items)
      this.isRelevancyEnabled = !!response.body?.relevance || this.sourcesHaveRelevanceScores();
      
      // Check if release_date is available - prioritize actual metadata presence over API flag
      this.isDateEnabled = this.checkIfDateAvailableInMetadata() || response.body?.release_date;

      this.processSourcesData();
    } else {
      this.sourcesData = [];
      this.isRelevancyEnabled = false;
      this.processSourcesData();
    }
    this.cdr.markForCheck();
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"handleMetadataResponse")
      return;
    }
  }

  /**
   * Reads a field from the legacy nested `metadata` object or, when absent, from the flat item.
   * Supports both the nested and flattened source metadata schemas.
   */
  private readMetadataField(item: Record<string, any>, field: string): any {
    const nested = item['metadata'];
    const nestedValue = nested && typeof nested === 'object' ? nested[field] : undefined;
    return nestedValue ?? item[field];
  }

  private normalizeAgentSourceItem(item: SourceData): SourceData {
    const normalized = normalizeRegulationsCitation(item) ?? item;
    const result = { ...normalized } as SourceData;

    const nested = result['metadata'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested) && 'band' in nested) {
      const nestedWithoutBand = { ...(nested as Record<string, unknown>) };
      delete nestedWithoutBand['band'];
      result['metadata'] = nestedWithoutBand;
    }

    const band = result['band'];
    if (!result['relevance_score'] && typeof band === 'string') {
      const bandNormalized = band.trim().toUpperCase();
      if (SourcePanelComponent.RELEVANCE_BANDS.includes(bandNormalized)) {
        result['relevance_score'] = bandNormalized;
      }
    }
    delete result['band'];
    SourcePanelComponent.AGENT_HIDDEN_FIELDS.forEach(field => delete result[field]);
    return result;
  }

  private sourcesHaveRelevanceScores(): boolean {
    if (!this.sourcesData?.length) {
      return false;
    }
    const data = Array.isArray(this.sourcesData) ? this.sourcesData : [this.sourcesData];
    return data.some((source) => {
      const score = source?.['relevance_score'];
      if (typeof score !== 'string' || !score.trim()) {
        return false;
      }
      return SourcePanelComponent.RELEVANCE_BANDS.includes(score.trim().toUpperCase());
    });
  }

  /**
   * Check if any metadata items have release_date data
   * Scans through sources data to determine if date sorting should be enabled
   * @private
   * @returns boolean - True if any metadata item contains release date information
   */
  private checkIfDateAvailableInMetadata(): boolean {
    try{
    if (!this.sourcesData || this.sourcesData.length === 0) {
      return false;
    }

    return this.sourcesData.some(item => {
      // Check for direct release_date field
      if (item['release_date']) {
        return true;
      }
      
      // Check for nested metadata.release_date field
      if (item['metadata'] && item['metadata']['release_date']) {
        return true;
      }
      
      return false;
    });
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"checkIfDateAvailableInMetadata")
      return false;
    }
  }


  /**
   * Handle metadata fetch error
   * Resets component state and logs error when metadata API call fails
   * @param error - Error object from failed API call
   * @returns void
   */
  public handleMetadataError(error: any): void {
    this.isLoading = false;
    this.sourcesData = [];
    this.isRelevancyEnabled = false;
    this.processSourcesData();
    this.loggerService.logError(this.SourcePanelError(error), "handleMetadataError")
    this.cdr.markForCheck();
  }

  /**
   * Process the fetched sources data and update component state
   * Transforms sources data, calculates counts, and updates relevancy information
   * @returns void
   */
  public processSourcesData(): void {
    try{
    if (!this.sourcesData || (Array.isArray(this.sourcesData) && this.sourcesData.length === 0)) {
      this.sources = [];
      this.sourcesCount = 0;
      this.cachedSourcesWithKeys = [];
      // Reset relevancy data when no sources
      this.relevancyData = {
        mostRelevant: 0,
        mediumRelevant: 0,
        lessRelevant: 0
      };
      return;
    }

    // Ensure this.sources is always an array
    this.sources = Array.isArray(this.sourcesData) ? this.sourcesData : [this.sourcesData];
    this.sourcesCount = this.calculateOptimizedSourcesCount();

    // Calculate relevancy data only if relevancy is enabled
    if (this.isRelevancyEnabled) {
      this.calculateRelevancyData();
    } else {
      // Reset relevancy data when disabled
      this.relevancyData = {
        mostRelevant: 0,
        mediumRelevant: 0,
        lessRelevant: 0
      };
    }
    this.cachedSourcesWithKeys = this.getSourcesWithKeys();
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"processSourcesData")
    }
  }

  /**
   * Calculate the optimized count of sources based on unique identifiers
   * Counts valid source items that have ID fields (id, ins_id, or rtq_id)
   * @returns number - Total count of valid sources
   */
  public calculateOptimizedSourcesCount(): number {
    try{
    if (!this.sourcesData) {
      return 0;
    }

    // With the new API structure, each item in sourcesData represents a single source
    // Count items that have either id field or the old ins_id/rtq_id fields
    return Array.isArray(this.sourcesData)
      ? this.sourcesData.filter(item => item['id'] || item['ins_id'] || item['rtq_id']).length
      : ((this.sourcesData['id'] || this.sourcesData['ins_id'] || this.sourcesData['rtq_id']) ? 1 : 0);
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"calculateOptimizedSourcesCount")
      return 0;
    }
  }

  /**
   * Close the source panel and emit close event
   * Emits closePanelEvent to notify parent component to hide the panel
   * @returns void
   */
  public closePanel(): void {
    this.closePanelEvent.emit();
  }

  /**
   * Filter sources based on search query
   * Triggers metadata fetch with current search query and selected filters
   * @returns void
   */
  public filterSources(): void {
    const relevance = this.getRelevanceValue(this.selectedFilter);
    this.fetchSourceMetadata(this.tenantId, this.searchQuery, this.chatHistoryId, relevance, this.sortBy);
  }

  /**
   * Set the view mode for displaying sources
   * @param mode - The view mode to set
   */
  public setViewMode(mode: ViewMode): void {
    if (this.viewMode !== mode) {
      this.viewMode = mode;
      this.logViewModeChange(mode);
    }
  }

  /**
   * Log view mode change details for analytics and debugging
   * @param mode - The new view mode that was set
   * @returns void
   */
  public logViewModeChange(mode: ViewMode): void {
    if (mode === 'compact') {
    } else if (mode === 'detailed') {
    }
    
    // Log the total number of sources being displayed
    const totalSources = this.sourcesData?.length || 0;
  }

  /**
   * View or download a file based on configuration
   * @param fileName - Name of the file to view/download
   * @param actionType - Type of action to perform
   * @param insightId - Insight identifier
   */
  public viewFile(fileName: string, actionType: any, insightId: any, event: Event): void {
    try{
    event.preventDefault();
    event.stopPropagation();
    
    // Only proceed on mouseup to avoid duplicate triggers
    if (event.type !== 'mouseup' || (event as MouseEvent).button !== 0) {
      return;
    }
    
    if (!this.validateViewFileParameters(fileName, insightId)) {
      return;
    }
    const scrollPosition = this.scrollContainer?.nativeElement.scrollTop || 0;
    this.preventScroll();

    this.sourceApi.ViewInsightFile(fileName, this.tenantId, insightId)
      .subscribe({
        next: (response) => {
          this.handleViewFileResponse(response, fileName);
          this.restoreScrollPosition(scrollPosition);
        },
        error: (error) => {
          this.loggerService.logError(this.SourcePanelError(error), "viewFile")
          this.restoreScrollPosition(scrollPosition);
        }
      });
    }
    catch(error:any){
      this.loggerService.logError(this.SourcePanelError(error),"viewFile")
    }
  }

  /**
   * Validate parameters for viewing file
   * Ensures required parameters are present before attempting file operations
   * @param fileName - Name of the file to validate
   * @param insightId - Insight identifier to validate
   * @returns boolean - True if parameters are valid, false otherwise
   */
  public validateViewFileParameters(fileName: string, insightId: any): boolean {
    return !!(fileName && insightId && this.tenantId);
  }

  /**
   * Handle successful view file response
   * Routes to appropriate download method based on configuration settings
   * @param response - API response containing file data or download URL
   * @param fileName - Name of the file being processed
   * @returns void
   */
  public handleViewFileResponse(response: any, fileName: string): void {
    if (this.configdata?.DownloadApiToggle) {
      this.handleDownloadApiResponse(response, fileName);
    } else {
      this.handleDirectDownload(response, fileName);
    }
  }

  /**
   * Handle download via API response
   */
  public handleDownloadApiResponse(response: any, fileName: string): void {
    try{
    if (!response) {
      this.loggerService.logError("Invalid response for handleDownloadApiResponse","handleDownloadApiResponse")
      return;
    }

    const downloadUrl = response;

    fetch(downloadUrl)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        return res.blob();
      })
      .then(blob => this.downloadBlob(blob, fileName))
      .catch(error => this.loggerService.logError(this.SourcePanelError(error), "handleDownloadApiResponse"));
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"handleDownloadApiResponse")
    }
  }

  /**
   * Handle direct download response
   */
  public handleDirectDownload(response: any, fileName: string): void {
    this.downloadBlob(response, fileName);
  }

  /**
   * Download blob as file
   */
  public downloadBlob(blob: Blob, fileName: string): void {
    try{
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"downloadBlob")
    }
  }

  /**
   * Handle view file error
   */
  public handleViewFileError(error: any): void {
    this.loggerService.logError(this.SourcePanelError(error), "handleViewFileError")
  }

  /**
   * Generate insight view URL for href attribute
   * @param insightId - Insight identifier
   * @returns Complete URL for insight view
   */
  public getInsightViewURLForHref(insightId: string): string {
    return this.InsightViewURL + window.btoa(encodeURIComponent(insightId));
  }

  /**
   * Download document by document ID
   * @param documentId - Document identifier
   */
  public downloadDocument(documentId: string): void {
    if (!documentId) {
      return;
    }

    this.sourceApi.DownloadRtqFile(documentId).subscribe({
      next: (data: ApiResponse) => this.handleDownloadResponse(data, documentId),
      error: (error) => this.handleDownloadError(error)
    });
  }

  /**
   * Handle download document response
   */
  public handleDownloadResponse(data: ApiResponse, documentId: string): void {
    if (data.code === SourcePanelComponent.SUCCESS_STATUS_CODE && data.data) {
      this.downloadFileFromUrl(data.data, documentId);
    } else {
      this.loggerService.logError(this.SourcePanelError(data),"handleDownloadResponse")
    }
  }

  /**
   * Handle download document error
   */
  public handleDownloadError(error: any): void {
    this.loggerService.logError(this.SourcePanelError(error), "handleDownloadError")
  }

  /**
   * Download file from URL
   * @param url - File URL
   * @param documentId - Document identifier for filename
   */
  public downloadFileFromUrl(url: string, documentId: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = documentId;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Open document chat for specified insight
   * @param displayName - Display name of the document
   * @param insightId - Insight identifier
   */
  public DocChat(displayName: any, insightId: any, event: Event): void {
    try{
    event.preventDefault();
    event.stopPropagation();
    
    // Only proceed on mouseup to avoid duplicate triggers
    if (event.type !== 'mouseup' || (event as MouseEvent).button !== 0) {
      return;
    }
    
    if (!displayName || !insightId) {
      return;
    }
    const scrollPosition = this.scrollContainer?.nativeElement.scrollTop || 0;
    this.preventScroll();

    this.sourceApi.GetDocChatUrl(displayName, insightId)
      .subscribe({
        next: (data: ApiResponse) => {
          this.handleDocChatResponse(data);
          this.restoreScrollPosition(scrollPosition);
        },
        error: (error) => {
          this.loggerService.logError(this.SourcePanelError(error), "handleDocChatResponse")
          this.restoreScrollPosition(scrollPosition);
        }
      });
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"DocChat")
    }
  }

  /**
   * Handle document chat response
   */
  private handleDocChatResponse(data: any): void {
    if (data.status === '200' && data.response) {
      let EncyptedFileName = data.response;
      window.open(EncyptedFileName, '_blank');
    } else {
      this.loggerService.logError(this.SourcePanelError(data),"handleDocChatResponse")
    }
  }

  /**
   * Display countries with proper formatting
   * @param countries - Array of country names
   * @returns Formatted country display object
   */
  public displayCountries(countries: string[]): CountryDisplay {
    if (!countries || countries.length === 0) return { firstTwo: '', remainingCount: 0 };

    const countryArray = countries.map(country => country.trim());
    const firstTwo = countryArray.slice(0, 2).join(', ');
    const remainingCount = Math.max(0, countryArray.length - 2);

    return { firstTwo, remainingCount };
  }

  /**
   * Toggle the relevancy dropdown open/close state
   */
  public toggleRelevancyDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isRelevancyEnabled) {
      return;
    }
    
    // Close other dropdowns before toggling this one
    this.isSortDropdownOpen = false;
    this.isFilterDropdownOpen = false;
    
    this.isRelevancyDropdownOpen = !this.isRelevancyDropdownOpen;
  }

  /**
   * Close the relevancy dropdown when clicking outside
   */
  public closeRelevancyDropdown(): void {
    if (!this.isRelevancyEnabled) {
      return;
    }
    this.isRelevancyDropdownOpen = false;
  }

  /**
   * Calculate relevancy percentages based on metadata relevance scores
   */
  public calculateRelevancyData(): void {
    try{
    if (!this.isRelevancyEnabled || !this.sourcesData || (Array.isArray(this.sourcesData) && this.sourcesData.length === 0)) {
      this.relevancyData = { mostRelevant: 0, mediumRelevant: 0, lessRelevant: 0 };
      return;
    }

    const relevanceCounters = { high: 0, medium: 0, low: 0 };
    let totalCount = 0;

    const dataToProcess = Array.isArray(this.sourcesData) ? this.sourcesData : [this.sourcesData];

    dataToProcess.forEach(source => {
      totalCount++;
      const score = source?.['relevance_score']?.toLowerCase();
      if (score in relevanceCounters) {
        relevanceCounters[score as keyof typeof relevanceCounters]++;
      }
    });

    if (totalCount === 0) {
      this.relevancyData = { mostRelevant: 0, mediumRelevant: 0, lessRelevant: 0 };
      return;
    }

    const rawPercentages = {
      mostRelevant: Math.round((relevanceCounters.high / totalCount) * 100),
      mediumRelevant: Math.round((relevanceCounters.medium / totalCount) * 100),
      lessRelevant: Math.round((relevanceCounters.low / totalCount) * 100)
    };

    this.relevancyData = this.adjustPercentagesToTotal(rawPercentages);
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"calculateRelevancyData")
      return;
    }
  }

  /**
   * Adjust percentages to ensure they total 100% by distributing rounding errors
   * @param percentages - Raw percentage calculations
   * @returns Adjusted percentages that sum to 100%
   */
  public adjustPercentagesToTotal(percentages: { mostRelevant: number; mediumRelevant: number; lessRelevant: number }): typeof percentages {
    try{
    const total = percentages.mostRelevant + percentages.mediumRelevant + percentages.lessRelevant;

    if (total === 100) {
      return percentages;
    }

    const difference = 100 - total;
    const adjusted = { ...percentages };

    // Find the category with the highest percentage and adjust it
    const maxCategory = Object.keys(adjusted).reduce((a, b) =>
      adjusted[a as keyof typeof adjusted] > adjusted[b as keyof typeof adjusted] ? a : b
    ) as keyof typeof adjusted;

    adjusted[maxCategory] += difference;

    return adjusted;
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"adjustPercentagesToTotal")
      return percentages;
    }
  }

  // Lookup map for relevance classes
  public static readonly RELEVANCE_CLASS_MAP: Record<string, string> = {
    high: 'high',
    medium: 'medium',
    low: 'low'
  };

  /**
   * Get CSS class for relevance score
   * @param relevanceScore - The relevance score (high, medium, low)
   * @returns CSS class string
   */
  public getRelevanceClass(relevanceScore: string): string {
    if (!this.isRelevancyEnabled) {
      return '';
    }
    return relevanceScore
      ? SourcePanelComponent.RELEVANCE_CLASS_MAP[relevanceScore.toLowerCase()] || ''
      : '';
  }

  /**
   * Get flattened list of metadata items with their original keys
   * @returns Array of objects containing item data and their metadata keys
   */
  public getSourcesWithKeys(): { item: any, key: string }[] {
    try{
    if (!this.sources || !Array.isArray(this.sources) || this.sources.length === 0) {
      return [];
    }

    const itemsWithKeys: { item: any, key: string }[] = [];

    this.sources.forEach((sourceItem, index) => {
      if (sourceItem && typeof sourceItem === 'object') {
        // With the new API structure, each item should have an id field and be a direct source
        if (sourceItem['id'] || sourceItem['ins_id'] || sourceItem['rtq_id']) {
          itemsWithKeys.push({
            item: sourceItem,
            // Use the original_key if available, otherwise use the index or key field
            key: sourceItem['original_key'] || sourceItem['key'] || (index + 1).toString()
          });
        }
      }
    });

    return itemsWithKeys;
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"getSourcesWithKeys")
      return [];
    }
  }

  /**
   * TrackBy function for cachedSourcesWithKeys *ngFor to prevent unnecessary DOM re-creation.
   */
  public trackBySourceKey(index: number, item: { item: any, key: string }): string {
    return item.key;
  }

  /**
   * Document rows for DOC_TABLE: prefers documents[]; falls back to attachment_name.
   */
  public getDocumentRows(item: SourceData): RegulationDocumentRow[] {
    return getRegulationDocumentRows(item);
  }

  /**
   * @deprecated Prefer getDocumentRows. Kept for specs that assert legacy string parsing.
   */
  public getAttachments(item: SourceData, fieldKey: string): string[] {
    void fieldKey;
    return this.getDocumentRows(item).map((row) => row.fileName);
  }

  /**
   * Visible document rows for a source item (first 3 unless expanded).
   */
  public getVisibleDocumentRows(itemKey: string, rows: RegulationDocumentRow[]): RegulationDocumentRow[] {
    if (!rows?.length) {
      return [];
    }
    if (this.isDocumentsExpanded(itemKey) || rows.length <= this.DOC_TABLE_PREVIEW_COUNT) {
      return rows;
    }
    return rows.slice(0, this.DOC_TABLE_PREVIEW_COUNT);
  }

  /**
   * @deprecated Prefer getVisibleDocumentRows.
   */
  public getVisibleAttachments(itemKey: string, attachments: string[]): string[] {
    if (!attachments?.length) {
      return [];
    }
    if (this.isDocumentsExpanded(itemKey) || attachments.length <= this.DOC_TABLE_PREVIEW_COUNT) {
      return attachments;
    }
    return attachments.slice(0, this.DOC_TABLE_PREVIEW_COUNT);
  }

  public isDocumentsExpanded(itemKey: string): boolean {
    return this.expandedDocumentKeys.has(itemKey);
  }

  public toggleDocumentsExpanded(itemKey: string): void {
    if (this.expandedDocumentKeys.has(itemKey)) {
      this.expandedDocumentKeys.delete(itemKey);
    } else {
      this.expandedDocumentKeys.add(itemKey);
    }
    this.cdr.markForCheck();
  }

  public trackByDocumentRow(index: number, row: RegulationDocumentRow): string {
    return row?.fileName || String(index);
  }

  public trackByAttachment(index: number, attach: string): string {
    return attach || String(index);
  }

  /** Display label with numeric id prefix stripped; full name still used for download/DocChat. */
  public getAttachmentDisplayName(fileName: string): string {
    return formatAttachmentDisplayName(fileName);
  }

  /** Ver. / Released cell: value or em dash. */
  public formatDocTableCell(value: string): string {
    return formatDocTableCell(value, this.DOC_TABLE_PLACEHOLDER);
  }

  /** Released cell: normalize to DD-MM-YYYY (strip ISO time). */
  public formatDocReleaseDate(value: string): string {
    return formatDocReleaseDate(value, this.DOC_TABLE_PLACEHOLDER);
  }

  /**
   * Parse reference URL field (array or ##~-delimited string) into a clean list.
   */
  public getReferenceUrls(item: SourceData, fieldKey: string): string[] {
    const value = this.getFieldValue(item, fieldKey);
    if (Array.isArray(value)) {
      return value
        .filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
        .map((url) => url.trim());
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.split('##~').map((url) => url.trim()).filter((url) => url.length > 0);
    }
    return [];
  }

  /**
   * Visible reference URLs for a source field (first 2 unless expanded).
   */
  public getVisibleReferenceUrls(itemKey: string, fieldKey: string, urls: string[]): string[] {
    if (!urls?.length) {
      return [];
    }
    if (this.isReferencesExpanded(itemKey, fieldKey) || urls.length <= this.REF_LINKS_PREVIEW_COUNT) {
      return urls;
    }
    return urls.slice(0, this.REF_LINKS_PREVIEW_COUNT);
  }

  public isReferencesExpanded(itemKey: string, fieldKey: string): boolean {
    return this.expandedReferenceKeys.has(this.buildReferenceExpandKey(itemKey, fieldKey));
  }

  public toggleReferencesExpanded(itemKey: string, fieldKey: string): void {
    const key = this.buildReferenceExpandKey(itemKey, fieldKey);
    if (this.expandedReferenceKeys.has(key)) {
      this.expandedReferenceKeys.delete(key);
    } else {
      this.expandedReferenceKeys.add(key);
    }
    this.cdr.markForCheck();
  }

  public trackByReferenceUrl(index: number, url: string): string {
    return url || String(index);
  }

  private buildReferenceExpandKey(itemKey: string, fieldKey: string): string {
    return `${itemKey}::${fieldKey}`;
  }

  /**
   * Toggle the filter dropdown open/close state
   */
  public toggleFilterDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isRelevancyEnabled) {
      return;
    }
    
    // Close other dropdowns before toggling this one
    this.isSortDropdownOpen = false;
    this.isRelevancyDropdownOpen = false;
    
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
  }

  /**
   * Select a filter option and apply filtering
   * @param filter - The selected filter option
   */
  public selectFilter(filter: string, event?: Event): void {
    try{
    if (event) {
      event.stopPropagation();
    }
    if (!this.isRelevancyEnabled && filter !== 'ALL') {
      return;
    }
    this.selectedFilter = filter;
    this.isFilterDropdownOpen = false;

    // Apply the selected filter
    const relevance = this.getRelevanceValue(filter);
    this.fetchSourceMetadata(this.tenantId, this.searchQuery, this.chatHistoryId, relevance, this.sortBy);
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"selectFilter")
    }
  }

  /**
   * Get relevance value for API filtering
   * @param filter - The selected filter option
   * @returns Relevance value for API call
   */
  public getRelevanceValue(filter: string): string | null {
    if (!this.isRelevancyEnabled) {
      return null;
    }

    if (filter === 'ALL') {
      return null;
    }

    return SourcePanelComponent.FILTER_RELEVANCE_MAP[filter] || null;
  }

  /**
   * Get relevance dot class for selected filter display
   * @param filter - The selected filter option
   * @returns CSS class for relevance dot
   */
  public getRelevanceDotClass(filter: string): string {
    if (!this.isRelevancyEnabled) {
      return '';
    }
    return SourcePanelComponent.FILTER_RELEVANCE_MAP[filter] || '';
  }

  /**
   * Prevent browser scroll behavior
   */
  public preventScroll(): void {
    try{
    if (this.scrollContainer) {
      const scrollableElement = this.scrollContainer.nativeElement;
      const currentScroll = scrollableElement.scrollTop;

      const blockScroll = (e: Event) => {
        scrollableElement.scrollTop = currentScroll;
        e.preventDefault();
      };

      scrollableElement.addEventListener('scroll', blockScroll, { passive: false });

      setTimeout(() => {
        scrollableElement.removeEventListener('scroll', blockScroll);
      }, 100);
    }
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"preventScroll")
    }
  }

  /**
   * Restore the scroll position of the scroll container
   * @param scrollPosition - The scroll position to restore
   */
  public restoreScrollPosition(scrollPosition: number): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollTop = scrollPosition;
    }
  }

  /**
   * Toggle the sort dropdown open/close state
   */
  public toggleSortDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.isRelevancyEnabled && !this.isDateEnabled) {
        return; // Prevent opening if both options are disabled
    }
    
    // Close other dropdowns before toggling this one
    this.isFilterDropdownOpen = false;
    this.isRelevancyDropdownOpen = false;
    
    this.isSortDropdownOpen = !this.isSortDropdownOpen;
  }

  /**
   * Select a sort option and trigger metadata fetch
   * @param value - The selected sort option value
   */
  public selectSortOption(value: string, event?: Event): void {
    try{
    if (event) {
      event.stopPropagation();
    }
    // Always set the selected option (no toggle behavior)
    this.selectedOption = value;
    this.isSortDropdownOpen = false; // Close the dropdown
    // Update sortBy and fetch data
    this.sortBy = this.selectedOption || '';
    const relevance = this.getRelevanceValue(this.selectedFilter);
    this.fetchSourceMetadata(this.tenantId, this.searchQuery, this.chatHistoryId, relevance, this.sortBy);
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"selectSortOption")
    }
  }

  /**
   * Get the display label for a sort option
   * @param value - The sort option value
   * @returns The translated label
   */
  

  public getOptionLabel(value: string | null): string {
    return this.options.find(opt => opt.value === value)?.label || '';
  }

  public dateFormater(value:string):string{
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        return date.toLocaleString('en-US', {
            month: 'short', // e.g., "Jul"
            day: 'numeric', // e.g., "1"
            year: 'numeric', // e.g., "2025"
             
        }).replace(/,/, ','); // Ensure comma after year
    } catch (error) {
      this.loggerService.logError(this.SourcePanelError(error),"dateFormater")
        return 'Invalid Date';
    } 
  }

  /**
   * Host listener to detect clicks outside the component
   * Closes dropdowns when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Check if the click is outside the sort dropdown
    if (this.isSortDropdownOpen) {
      const sortDropdown = this.elementRef.nativeElement.querySelector('.source-panel__sort-dropdown');
      if (sortDropdown && !sortDropdown.contains(target)) {
        this.isSortDropdownOpen = false;
      }
    }
    
    // Check if the click is outside the filter dropdown
    if (this.isFilterDropdownOpen) {
      const filterDropdown = this.elementRef.nativeElement.querySelector('.source-panel__filter-dropdown');
      if (filterDropdown && !filterDropdown.contains(target)) {
        this.isFilterDropdownOpen = false;
      }
    }
    
    // Check if the click is outside the relevancy dropdown
    if (this.isRelevancyDropdownOpen) {
      const relevancyDropdown = this.elementRef.nativeElement.querySelector('.relevancy-dropdown');
      if (relevancyDropdown && !relevancyDropdown.contains(target)) {
        this.isRelevancyDropdownOpen = false;
      }
    }
  }

  /**
   * Get module name from source item
   */
  public getModuleName(item: SourceData): string | null {
    try{
    // First try to get module_name directly from the item (new API structure)
    if (item?.['module_name']) {
      return item['module_name'];
    }
    
    // Fallback: Map based on sourceContext.module_name to our configuration module names
    if (this.sourceContext?.module_name === 'regulations' || this.sourceContext?.module_name === 'view_access_to_regulations') {
      return 'regulations';
    } else if (this.sourceContext?.module_name === 'rtq' || this.sourceContext?.module_name === 'view_access_to_rtq') {
      return 'rtq';
    }
    
    // If no mapping found, return null (will use legacy rendering)
    return null;
  }
  catch(error: any) {
    this.loggerService.logError(this.SourcePanelError(error),"getModuleName")
    return null;
  }
  }

  /**
   * Get field value using the configuration service
   */
  public getFieldValue(item: SourceData, fieldKey: string): any {
    return this.metadataConfigService.getFieldValue(item, fieldKey);
  }

  /**
   * Check if field has value
   */
  public hasFieldValue(item: SourceData, fieldKey: string): boolean {
    return this.metadataConfigService.hasFieldValue(item, fieldKey);
  }

  /**
   * Get formatted field value - enhanced version
   */
  public getFormattedFieldValue(item: SourceData, field: MetadataFieldConfig): any {
    const value = this.getFieldValue(item, field.key);
    return this.metadataConfigService.formatFieldValueEnhanced(value, field);
  }

  /**
   * Get visible fields for compact view - uses universal field discovery
   * For compact view, countries are handled in location section, so return empty array
   */
  public getCompactFields(item: SourceData): MetadataFieldConfig[] {
    // Use the new universal configuration system
    const moduleName = this.getModuleName(item);
    return this.metadataConfigService.discoverAvailableFields(item, 'compact', moduleName || undefined);
  }

  /**
   * Get visible fields for detailed view - uses universal field discovery
   * Returns all available fields except countries (handled in location section) and dates (removed per requirement)
   */
  public getDetailedFields(item: SourceData): MetadataFieldConfig[] {
    // Use the new universal configuration system
    const moduleName = this.getModuleName(item);
    return this.metadataConfigService.discoverAvailableFields(item, 'detailed', moduleName || undefined);
  }

  /**
   * Generate view URL for an item
   */
  public getViewUrl(item: SourceData): string | null {
    try{
    const moduleName = this.getModuleName(item);
    if (moduleName === 'regulations' && item.ins_id) {
      if (this.regulationNextService.hasRegulationNextAccess()) {
        // Version deep-link is agent-only; chatbot sources keep the versionless link.
        const version = this.isAgentSourceApi ? resolveRegulationVersion(item) : '';
        return this.regulationNextService.buildRegulationNextUrl(
          this.configdata?.FusionAppUIUrl ?? '',
          item.ins_id,
          version
        );
      }
      return this.regulationNextService.buildLegacyRegulationUrl(this.configdata?.FusionAppUIUrl ?? '', item.ins_id);
    } else if (moduleName === 'rtq' && item.rtq_id) {
      // For RTQ, build the URL
      return this.configdata?.FusionAppUIUrl + '/rtq?modulePath=/rtqs/view-rtq&id=' + item.rtq_id;
    }
    
    return null;
  }
  catch(error: any) {
    this.loggerService.logError(this.SourcePanelError(error),"getViewUrl")
    return null;
  }
  }

  /**
   * Get display title (backwards compatible)
   */
  public getDisplayTitle(item: SourceData): string {
    try{
    // Check multiple possible title fields in order of preference
    const titleFields = [
      'title',
      'insight_title',
      'metadata.title',
      'metadata.insight_title',
      'query_title',
      'metadata.query_title'
    ];
    
    for (const field of titleFields) {
      const value = this.getFieldValue(item, field);
      if (value && value.trim()) {
        return value;
      }
    }
    
    return '';
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"getDisplayTitle")
      return '';
    }
  }

  /**
   * Get display ID (backwards compatible)
   */
  public getDisplayId(item: SourceData): string {
    try{
    // Check multiple possible ID fields in order of preference
    const idFields = [
      'id',
      'ins_id',
      'rtq_id',
      'metadata.encrypted_regulation_id',
      'metadata.reference_insight_id'
    ];
    
    for (const field of idFields) {
      const value = this.getFieldValue(item, field);
      if (value && value.trim()) {
        return value;
      }
    }
    
    return '';
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"getDisplayId")
      return '';
    }

  }

  /**
   * Get all available fields for an item regardless of module name
   * This method uses the universal configuration system
   */
  public getAllAvailableFields(item: SourceData, viewMode: 'compact' | 'detailed'): any[] {
    try{
    const moduleName = this.getModuleName(item);
    const fields = this.metadataConfigService.discoverAvailableFields(item, viewMode, moduleName || undefined);
    
    // Convert to the legacy format for backwards compatibility
    return fields.map(field => ({
      key: field.key,
      label: field.label,
      value: this.getFieldValue(item, field.key),
      type: field.type,
      displayInView: viewMode
    }));
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"getAllAvailableFields")
      return [];
    }
  }

  // Field formatting methods moved to MetadataConfigService for centralized management

  /**
   * Get countries for display from various possible field paths
   */
  public getCountriesForDisplay(item: SourceData): string[] {
    try{
    // Check multiple possible country field paths
    const countryFields = [
      'metadata.countries',
      'countries',
      'Country',
      'metadata.Country'
    ];
    
    for (const field of countryFields) {
      const value = this.getFieldValue(item, field);
      if (Array.isArray(value) && value.length > 0) {
        return value;
      } else if (typeof value === 'string' && value.trim()) {
        return [value];
      }
    }
    
    return [];
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"getCountriesForDisplay")
      return [];
    }
  }

  /**
   * Get brand names for display from various possible field paths
   */
  public getBrandNamesForDisplay(item: SourceData): string[] {
    try{
    // Check multiple possible brand name field paths
    const brandNameFields = [
      'metadata.brandNames',
      'brandNames',
      'brandName',
      'metadata.brandName'
    ];
    
    for (const field of brandNameFields) {
      const value = this.getFieldValue(item, field);
      if (Array.isArray(value) && value.length > 0) {
        return value;
      } else if (typeof value === 'string' && value.trim()) {
        return [value];
      }
    }
    
    return [];
    }
    catch(error: any) {
      this.loggerService.logError(this.SourcePanelError(error),"getBrandNamesForDisplay")
      return [];
    }
  }

  /**
   * Display brand names with proper formatting
   * @param brandNames - Array of brand names
   * @returns Formatted brand name display object
   */
  public displayBrandNames(brandNames: string[]): CountryDisplay {
    if (!brandNames || brandNames.length === 0) return { firstTwo: '', remainingCount: 0 };

    const brandNameArray = brandNames.map(brandName => brandName.trim());
    const firstTwo = brandNameArray.slice(0, 2).join(', ');
    const remainingCount = Math.max(0, brandNameArray.length - 2);

    return { firstTwo, remainingCount };
  }

  /**
   * Open view URL in a new tab; prevent default so href + target="_blank" does not double-open.
   */
  public onIdLinkClick(event: Event, item: SourceData): void {
    const url = this.getViewUrl(item);
    if (url && (event as MouseEvent).button === 0) {
      event.preventDefault();
      event.stopPropagation();
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Open reference URL in a new tab; prevent default so href + target="_blank" does not double-open.
   */
  public onReferenceClick(event: Event, url: string): void {
    if (url && (event as MouseEvent).button === 0) {
      event.preventDefault();
      event.stopPropagation();
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /** Reasoning / explanation copy for the info icon beside the insight ID. */
  public getSourceReasoning(item: SourceData): string {
    const explanation = this.getFieldValue(item, 'explanation');
    if (typeof explanation === 'string' && explanation.trim()) {
      return explanation.trim();
    }
    const reasoning = this.getFieldValue(item, 'reasoning');
    return typeof reasoning === 'string' ? reasoning.trim() : '';
  }

  public hasSourceReasoning(item: SourceData): boolean {
    return this.getSourceReasoning(item).length > 0;
  }
}